// pages/mobile/SetupPage.js
// One-time phone setup (mockups 1b, 1d, 1e): signed in, install the app,
// allow notifications. Android can install from our button; iPhone has no
// install API, so it gets a step-by-step guide instead. Allowing notifications
// also connects the phone to push (pushSubscription.js).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TabBar } from './MobileChrome';
import { AddSquareIcon, BellIcon, CheckIcon, DownloadIcon, InfoIcon, ShareIcon } from './icons';
import {
  canPromptInstall,
  isInAppBrowser,
  isInstalled,
  isIos,
  notificationPermission,
  promptInstall,
  requestNotificationPermission,
  subscribeToInstallState,
} from './installState';
import { ensurePushSubscription, sendTestNotification } from './pushSubscription';
import { errorMessage } from './mobileApi';
import styles from './mobile.module.css';

const Step = ({ number, done, current, title, children }) => (
  <div className={current ? `${styles.step} ${styles.stepCurrent}` : styles.step}>
    <div className={styles.stepHeader}>
      <span className={done ? `${styles.stepMarker} ${styles.stepDone}` : styles.stepMarker}>
        {done ? <CheckIcon size={16} /> : number}
      </span>
      <span className={styles.stepText}>
        <span className={styles.stepTitle}>{title}</span>
        {children}
      </span>
    </div>
  </div>
);

const IosGuide = ({ onClose }) => (
  <>
    <div className={styles.sheetBackdrop} onClick={onClose} aria-hidden="true" />
    <div role="dialog" aria-modal="true" aria-labelledby="ios-guide-title" className={styles.sheet}>
      <div className={styles.sheetHandle} />
      <div>
        <h2 id="ios-guide-title" className={styles.sheetTitle}>Add to your Home Screen</h2>
        <p className={styles.mutedText}>iPhone only sends dispatch alerts to apps on the Home Screen. Three taps:</p>
      </div>
      <ol className={styles.guideList}>
        <li className={styles.guideStep}><span className={styles.tile}><ShareIcon /></span>Tap <strong>Share</strong> in Safari's toolbar</li>
        <li className={styles.guideStep}><span className={styles.tile}><AddSquareIcon /></span>Scroll down, tap <strong>Add to Home Screen</strong></li>
        <li className={styles.guideStep}><span className={styles.tile}><CheckIcon /></span>Tap <strong>Add</strong>, then open Facilitate from the new icon</li>
      </ol>
      {isInAppBrowser() && (
        <div className={styles.banner}>
          <InfoIcon />
          <span>No Share button? You opened this inside another app. Tap <strong>Open in Safari</strong> first.</span>
        </div>
      )}
      <button type="button" className={styles.primaryButton} onClick={onClose}>Got it</button>
    </div>
  </>
);

// What ensurePushSubscription() found, as this page's push state.
const PUSH_STATES = { subscribed: 'on', not_configured: 'not_configured', unsupported: 'unsupported' };

const SetupPage = () => {
  const [installable, setInstallable] = useState(canPromptInstall());
  const [installed, setInstalled] = useState(isInstalled());
  const [permission, setPermission] = useState(notificationPermission());
  const [guideOpen, setGuideOpen] = useState(false);
  // idle | connecting | on | not_configured | unsupported | failed
  const [pushState, setPushState] = useState('idle');
  const [testResult, setTestResult] = useState(null);

  useEffect(
    () =>
      subscribeToInstallState(() => {
        setInstallable(canPromptInstall());
        setInstalled(isInstalled());
      }),
    []
  );

  const handleInstall = async () => {
    if (await promptInstall()) setInstalled(true);
  };

  const connectPush = useCallback(async () => {
    setPushState('connecting');
    try {
      const result = await ensurePushSubscription();
      setPushState(PUSH_STATES[result] || 'idle');
    } catch (error) {
      setPushState('failed');
    }
  }, []);

  useEffect(() => {
    if (notificationPermission() === 'granted') connectPush();
  }, [connectPush]);

  const handleAllow = async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'granted') connectPush();
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      await sendTestNotification();
      setTestResult({ ok: true, text: 'Sent. It should appear in a few seconds.' });
    } catch (error) {
      setTestResult({ ok: false, text: errorMessage(error) });
    }
  };

  const notificationsOn = permission === 'granted' && pushState === 'on';
  const doneCount = 1 + (installed ? 1 : 0) + (notificationsOn ? 1 : 0);

  let notificationHint = 'When your phone asks, tap Allow. Without this you won’t hear about dispatches.';
  if (pushState === 'failed') {
    notificationHint = "Notifications are allowed, but we couldn't connect this phone. Check your signal and try again.";
  } else if (pushState === 'connecting') {
    notificationHint = 'Connecting this phone…';
  } else if (pushState === 'unsupported') {
    notificationHint = "This browser can't get alerts. Open Facilitate from its Home Screen icon instead.";
  } else if (pushState === 'not_configured') {
    notificationHint = 'Allowed. Phone alerts start as soon as the office switches them on.';
  } else if (notificationsOn) {
    notificationHint = 'Dispatches and messages will alert this phone.';
  } else if (permission === 'denied') {
    notificationHint = 'Notifications are blocked. Turn them on for Facilitate in your phone settings.';
  } else if (permission === 'unsupported') {
    notificationHint = isIos() && !installed
      ? 'Add Facilitate to your Home Screen first, then open it from the icon.'
      : "This browser can't show notifications. Open Messages to check your dispatches.";
  }

  let installAction = null;
  if (!installed) {
    if (installable) {
      installAction = (
        <button type="button" className={styles.primaryButton} onClick={handleInstall}>
          <DownloadIcon />Install app
        </button>
      );
    } else if (isIos()) {
      installAction = (
        <button type="button" className={styles.primaryButton} onClick={() => setGuideOpen(true)}>Show me how</button>
      );
    } else {
      installAction = (
        <p className={styles.mutedText}>Open your browser menu and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</p>
      );
    }
  }

  return (
    <main className={`${styles.page} ${styles.pageWithTabs}`}>
      <header className={styles.pageHeader}>
        <p className={styles.greeting} style={{ fontWeight: 700, letterSpacing: 1.6, color: '#7D6224' }}>
          ONE-TIME SETUP · {doneCount} OF 3 DONE
        </p>
        <h1 className={styles.pageTitle}>Make sure you get dispatches</h1>
        <p className={styles.mutedText}>Set up this phone for dispatch alerts, then send a test notification.</p>
      </header>

      <div className={styles.content}>
        <div className={styles.stepList}>
          <Step number={1} done title="Signed in on this phone">
            <span className={styles.mutedText}>No password, and you stay signed in until you sign out.</span>
          </Step>

          <Step number={2} done={installed} current={!installed} title={installed ? 'Added to your Home Screen' : 'Install the Facilitate app'}>
            <span className={styles.mutedText}>
              {installed
                ? 'Open Facilitate from its icon, not the browser.'
                : 'Puts Facilitate on your Home Screen like any other app. iPhone only sends alerts to the Home Screen app.'}
            </span>
          </Step>
          {installAction}

          <Step number={3} done={notificationsOn} current={installed && !notificationsOn} title={notificationsOn ? 'Notifications are on' : 'Turn on notifications'}>
            <span className={styles.mutedText}>
              {notificationHint}
            </span>
          </Step>
          {permission === 'default' && (
            <button type="button" className={styles.primaryButton} onClick={handleAllow}>
              <BellIcon />Allow notifications
            </button>
          )}
          {pushState === 'failed' && (
            <button type="button" className={styles.primaryButton} onClick={connectPush}>
              <BellIcon />Try again
            </button>
          )}
          {pushState === 'on' && (
            <button type="button" className={styles.secondaryButton} onClick={handleTest}>
              <BellIcon />Send a test notification
            </button>
          )}
          {testResult && (
            <p role={testResult.ok ? 'status' : 'alert'} className={styles.mutedText}>{testResult.text}</p>
          )}
        </div>

        <p className={styles.mutedText} style={{ textAlign: 'center' }}>
          You can always check your dispatches in Messages. Email delivery is not available yet. <Link to="/m">Go to messages</Link>
        </p>
      </div>

      {guideOpen && <IosGuide onClose={() => setGuideOpen(false)} />}
      <TabBar />
    </main>
  );
};

export default SetupPage;
